import React, { useEffect, useState } from 'react';
import {
  Flex,
  Box,
  Stack,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  Spinner,
  Button as ChakraButton,
  FormControl,
  FormLabel,
  Switch,
  HStack,
  useToast,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { AxiosRequestConfig } from 'axios';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Heading, Input, Button, Textarea } from '~/components';
import { EquipmentTypeView, Parametros } from '~/types/equipmentType';
import { OrderControl } from '~/pages/Portal/Admin/Equipments/ConfigureTypes/OrderControl';
import { InputNewValue } from '~/components/InputNewValue';
import api from '~/utils/api';
import { queryClient } from '~/utils/react-query';
import { useEquipmentTypesPreview } from '~/hooks';
import { ConfirmationDialog } from '~/components/ConfirmationDialog';
import { InputFloat } from '~/components/InputFloat';

const ConfigureTypesFormSchema = yup.object().shape({
  nm_tipoequipamento: yup
    .string()
    .required('Campo obrigatório')
    .max(255, 'Máximo de 255 caracteres'),
  consumo_inicial_co2_kg: yup
    .number()
    .required('Campo obrigatório')
    .min(0.01, 'Quantidade deve ser maior que zero'),
  consumo_mensal_co2_kg: yup
    .number()
    .required('Campo obrigatório')
    .min(0.01, 'Quantidade deve ser maior que zero'),
});

export const ConfigureTypes: React.VFC<{ currentType: EquipmentTypeView }> = ({
  currentType,
}) => {
  const { formState, control, setError, clearErrors } = useForm({
    resolver: yupResolver(ConfigureTypesFormSchema),
  });
  const { errors } = formState;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenModal,
    onOpen: onOpenModal,
    onClose: onCloseModal,
  } = useDisclosure();
  const [status, setStatus] = useState(currentType?.tp_situacao === '1');
  const [listaParametros, setListaParametros] = useState<Parametros[]>(
    currentType?.parametros || [],
  );
  const [isFetching, setFetching] = useState(false);
  const [mostrarAdicionarParametro, setMostrarAdicionarParametro] =
    useState(false);
  const [editando, setEditando] = useState(false);
  const toast = useToast();
  const { data, refetch } = useEquipmentTypesPreview(
    listaParametros,
    currentType?.id_tipoequipamento,
  );
  const [descricao, setDescricao] = useState('');
  const [cd_equipamento, setSku] = useState('');

  const [tipoEquipamento, setTipoEquipamento] = useState<EquipmentTypeView>({
    ...currentType,
    nm_tipoequipamento: currentType?.nm_tipoequipamento || '',
    consumo_inicial_co2_kg:
      currentType?.consumo_inicial_co2_kg !== null
        ? currentType?.consumo_inicial_co2_kg
        : 0,
    consumo_mensal_co2_kg:
      currentType?.consumo_mensal_co2_kg !== null
        ? currentType?.consumo_mensal_co2_kg
        : 0,
  });

  useEffect(() => {
    refetch();
  }, [listaParametros]);

  useEffect(() => {
    setDescricao(data?.preview || '');
    setSku(data?.cd_equipamento || '');
  }, [data]);

  const possuiErros = () => {
    try {
      ConfigureTypesFormSchema.validateSync(tipoEquipamento, {
        abortEarly: false,
      });
      clearErrors();
    } catch (err) {
      if (err instanceof yup.ValidationError) {
        err.inner.forEach(e => {
          setError(e.path!, { message: e.message });
        });
        return true;
      }
    }
    return false;
  };

  const handleSave = () => {
    onCloseModal();
    if (possuiErros()) return;

    const lista = listaParametros.map(parametro => {
      return {
        nome: parametro.nome,
        status: parametro.status,
      };
    });

    const tipoEq = {
      id_tipoequipamento: currentType?.id_tipoequipamento,
      nm_tipoequipamento: tipoEquipamento.nm_tipoequipamento,
      tp_situacao: status ? 1 : 0,
      parametros: lista,
      consumo_inicial_co2_kg: tipoEquipamento.consumo_inicial_co2_kg,
      consumo_mensal_co2_kg: tipoEquipamento.consumo_mensal_co2_kg,
    };

    setFetching(true);

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const request = api.put(
      `/tipoequipamento/${currentType.id_tipoequipamento}`,
      tipoEq,
      config,
    );
    request
      .then(() => {
        queryClient.invalidateQueries('configureTypes');
        toast({
          title: 'Configuração do tipo de equipamento alterado',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
        onClose();
        queryClient.invalidateQueries('equipmentTypes');
      })
      .catch(err => {
        toast({
          title: `Erro ao configurar tipo de equipamento`,
          description: err[1] ? String(err[1]) : 'Erro desconhecido',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const guardarMudanca = (novaLista: Parametros[]) => {
    setListaParametros(novaLista);
  };

  const estaEditando = (edit: boolean) => {
    setEditando(edit);
  };

  const adicionarParametro = () => {
    setMostrarAdicionarParametro(!mostrarAdicionarParametro);
  };

  const adicionarNovoParametro = (texto: string) => {
    if (listaParametros.some(it => it.nome === texto)) {
      toast({
        title: 'Erro ao adicionar parâmetro',
        description: 'Já existe um parâmetro com esse nome',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const novoParametro: Parametros = {
      nome: texto,
      status: true,
    };
    const novaLista = [...listaParametros, novoParametro];
    setListaParametros([...novaLista]);
  };

  const handleOpen = () => {
    onOpen();
    setListaParametros(currentType?.parametros || []);
    setMostrarAdicionarParametro(false);
    setTipoEquipamento({
      ...currentType,
      nm_tipoequipamento: currentType?.nm_tipoequipamento || '',
      consumo_inicial_co2_kg:
        currentType?.consumo_inicial_co2_kg !== null
          ? currentType?.consumo_inicial_co2_kg
          : 0,
      consumo_mensal_co2_kg:
        currentType?.consumo_mensal_co2_kg !== null
          ? currentType?.consumo_mensal_co2_kg
          : 0,
    });
  };
  const handleClose = () => {
    clearErrors();
    onClose();
  };

  return (
    <>
      <Box textAlign="center">
        <ChakraButton
          fontSize="sm"
          variant="link"
          fontWeight="normal"
          color="inherit"
          textAlign="center"
          onClick={handleOpen}
          p={0}
        >
          Configurar
        </ChakraButton>
        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={handleClose}
          size="lg"
          closeOnOverlayClick={false}
        >
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton isDisabled={isFetching} />
            <DrawerHeader mb={4}>
              <Heading fontSize="xl">Configurar Equipamento</Heading>
            </DrawerHeader>

            <DrawerBody>
              <Flex flexDir="column" py="4" px="1" gridGap={6}>
                <Flex
                  flexDir={{ md: 'row', sm: 'column' }}
                  w="100%"
                  gridGap={4}
                >
                  <Stack gridGap={1} w="70%">
                    <FormControl isInvalid={errors?.nome}>
                      <FormLabel fontSize="sm" m={0}>
                        Nome *
                      </FormLabel>
                      <Input
                        name="nome"
                        type="text"
                        error={errors?.nome}
                        fontSize="15px"
                        onChange={e => {
                          setTipoEquipamento({
                            ...tipoEquipamento,
                            nm_tipoequipamento: e.target.value,
                          });
                          clearErrors('nm_tipoequipamento');
                        }}
                        value={tipoEquipamento.nm_tipoequipamento}
                        isDisabled={isFetching}
                      />
                    </FormControl>
                  </Stack>
                  <Stack gridGap={1} w="50%">
                    <FormLabel fontSize="sm" m={0}>
                      Situação
                    </FormLabel>
                    <FormControl
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      m={0}
                    >
                      <FormLabel htmlFor="status">
                        {status ? 'Ativo' : 'Inativo'}
                      </FormLabel>
                      <Switch
                        id="status"
                        size="lg"
                        colorScheme="green"
                        isChecked={status}
                        onChange={e => setStatus(e.target.checked)}
                        isDisabled={isFetching}
                      />
                    </FormControl>
                  </Stack>
                </Flex>

                <Flex flexDir="row">
                  <Stack gridGap={1} w="100%">
                    <Heading fontSize="md">Compensação de Carbono</Heading>
                    <HStack gridGap={3} alignItems="top">
                      <InputFloat
                        value={tipoEquipamento.consumo_inicial_co2_kg}
                        label="Consumo inicial (kg CO2e)"
                        name="consumo_inicial_co2_kg"
                        isRequired
                        error={errors?.consumo_inicial_co2_kg}
                        isDisabled={isFetching}
                        onChangeValue={e => {
                          clearErrors('consumo_inicial_co2_kg');
                          setTipoEquipamento({
                            ...tipoEquipamento,
                            consumo_inicial_co2_kg: e || 0,
                          });
                        }}
                      />
                      <InputFloat
                        value={tipoEquipamento.consumo_mensal_co2_kg}
                        label="Consumo mensal (kg CO2e/mês)"
                        name="consumo_mensal_co2_kg"
                        isRequired
                        error={errors?.consumo_mensal_co2_kg}
                        isDisabled={isFetching}
                        onChangeValue={e => {
                          clearErrors('consumo_mensal_co2_kg');
                          setTipoEquipamento({
                            ...tipoEquipamento,
                            consumo_mensal_co2_kg: e || 0,
                          });
                        }}
                      />
                    </HStack>
                  </Stack>
                </Flex>

                <Flex flexDir="row">
                  <Stack gridGap={1} w="100%">
                    <Heading fontSize="md">
                      {data?.cd_equipamento
                        ? `Preview da descrição (SKU: ${cd_equipamento})`
                        : 'Preview da descrição'}
                    </Heading>
                    <Textarea name="descricao" value={descricao} isReadOnly />
                  </Stack>
                </Flex>
                <Flex flexDir="row">
                  <HStack gridGap={1} w="100%" justifyContent="space-between">
                    <Heading fontSize="md">
                      Definir parâmetros de descrição
                    </Heading>
                    <Button
                      onClick={adicionarParametro}
                      isDisabled={editando || isFetching}
                    >
                      Adicionar
                    </Button>
                  </HStack>
                </Flex>
                {mostrarAdicionarParametro && !editando && (
                  <Stack>
                    <InputNewValue
                      label="Adicionar parâmetro"
                      placeholder="Insira novo parâmetro"
                      textoInformativo="A descrição deve ser igual ao SAP"
                      salvarValor={e => adicionarNovoParametro(e)}
                      esconderInput={adicionarParametro}
                      limiteCaracteres={50}
                    />
                  </Stack>
                )}
                <Stack>
                  <OrderControl
                    parametros={listaParametros}
                    salvarMudanca={guardarMudanca}
                    editando={estaEditando}
                    carregando={isFetching}
                  />
                </Stack>
              </Flex>
              <Flex flexDir="row" justifyContent="flex-end" gridGap={3}>
                <Button
                  type="submit"
                  onClick={handleClose}
                  color="black"
                  background="transparent"
                  style={{
                    marginTop: '20px',
                    marginLeft: '10px',
                  }}
                  isDisabled={isFetching}
                  _hover={{
                    bgColor: 'gray.300',
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  onClick={onOpenModal}
                  color="white"
                  background="gray.900"
                  _hover={{
                    bgColor: 'black',
                  }}
                  style={{ marginTop: '20px' }}
                  isDisabled={isFetching}
                >
                  Salvar Alterações
                  {isFetching && <Spinner style={{ marginLeft: '10px' }} />}
                </Button>
              </Flex>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Box>
      <ConfirmationDialog
        confirmar={handleSave}
        onClose={onCloseModal}
        isOpen={isOpenModal}
        titulo="Atenção"
      >
        <span>
          Ao continuar a descrição dos equipamentos serão impactadas. Tem
          certeza que deseja continuar?
        </span>
      </ConfirmationDialog>
    </>
  );
};
